import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import { Link } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import "./../../../assets/css/admin/dashbordAdmin/AdminDashboardPage.css";

import Breadcrumb from "../../../components/breadcrumb/Breadcrumb";
import useUtilisateur from "../../../hooks/useUtilisateur";
import useArticles from "../../../hooks/useArticle";
import usePaiement from "../../../hooks/usePaiement";
import useSignalement from "../../../hooks/useSignalement";
import useVisite from "../../../hooks/useVisite";
import useArticleEvent from "../../../hooks/useArticleEvent";
import useCategorie from "../../../hooks/useCategorie";
import { AuthContext } from "../../../contexts/AuthContext.js";

const MONTHS_SHORT_FR = [
  "Jan",
  "Fév",
  "Mar",
  "Avr",
  "Mai",
  "Juin",
  "Juil",
  "Août",
  "Sep",
  "Oct",
  "Nov",
  "Déc",
];
const MONTHS_LONG_FR = [
  "Janvier",
  "Février",
  "Mars",
  "Avril",
  "Mai",
  "Juin",
  "Juillet",
  "Août",
  "Septembre",
  "Octobre",
  "Novembre",
  "Décembre",
];
const TYPE_LABELS = {
  1: "Vues",
  2: "Clics",
  3: "Contacts",
  4: "Favoris",
  5: "Numéro consulté",
  6: "Contact WhatsApp",
  7: "Partagé Facebook",
};

/* ── Tooltip personnalisé Recharts ── */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + (p.value || 0), 0);
  return (
    <div
      style={{
        background: "var(--color-background-primary)",
        border: "0.5px solid var(--color-border-secondary)",
        borderRadius: 8,
        padding: "10px 14px",
        fontSize: 13,
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
      }}
    >
      <div
        style={{
          fontWeight: 500,
          marginBottom: 6,
          color: "var(--color-text-primary)",
        }}
      >
        {label}
      </div>
      {payload.map((p) => (
        <div
          key={p.dataKey}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 3,
          }}
        >
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              background: p.fill,
              display: "inline-block",
            }}
          />
          <span style={{ color: "var(--color-text-secondary)" }}>{p.name}</span>
          <span
            style={{
              fontWeight: 500,
              color: "var(--color-text-primary)",
              marginLeft: "auto",
              paddingLeft: 12,
            }}
          >
            {p.value}
          </span>
        </div>
      ))}
      <div
        style={{
          borderTop: "0.5px solid var(--color-border-tertiary)",
          marginTop: 6,
          paddingTop: 6,
          display: "flex",
          justifyContent: "space-between",
          color: "var(--color-text-secondary)",
          fontSize: 12,
        }}
      >
        <span>Total</span>
        <span style={{ fontWeight: 500, color: "var(--color-text-primary)" }}>
          {total}
        </span>
      </div>
    </div>
  );
};

/* ── Légende personnalisée Recharts ── */
const CustomLegend = () => (
  <div
    style={{
      display: "flex",
      gap: 18,
      fontSize: 12,
      color: "var(--color-text-secondary)",
      paddingBottom: 4,
    }}
  >
    {[
      { color: "#22c55e", label: "Activé" },
      { color: "#f59e0b", label: "En attente" },
      { color: "#ef4444", label: "Non activé" },
    ].map(({ color, label }) => (
      <span
        key={label}
        style={{ display: "flex", alignItems: "center", gap: 6 }}
      >
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: 2,
            background: color,
            display: "inline-block",
          }}
        />
        {label}
      </span>
    ))}
  </div>
);

export default function AdminDashboardPage({ setAlert }) {
  const { user } = useContext(AuthContext);
  const { getAllUser } = useUtilisateur();
  const { findAllArticlesForCategory, getArticlePublicationStats } =
    useArticles();
  const { getPaiements } = usePaiement();
  const [signalements, setSignalements] = useState([]);
  const { fetchSignalements } = useSignalement();
  const { getStatsVisites } = useVisite();

  const [visites, setVisites] = useState({
    total: 0,
    jour: 0,
    mois: 0,
    annee: 0,
  });

  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");

  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [articlesTotal, setArticlesTotal] = useState(0);
  const [paiements, setPaiements] = useState([]);

  /* ── Stats publication ── */
  const now = new Date();
  const [statMonth, setStatMonth] = useState(String(now.getMonth() + 1));
  const [statYear, setStatYear] = useState(String(now.getFullYear()));
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [publicationStats, setPublicationStats] = useState({
    data: [],
    total: 0,
    mode: "day",
  });
  const [statsLoading, setStatsLoading] = useState(false);

  const [articleStatsTable, setArticleStatsTable] = useState([]);
  const [articleTableLoading, setArticleTableLoading] = useState(false);
  const { getAllCategories } = useCategorie();

  const [articleEventStats, setArticleEventStats] = useState({
    byType: [],
    byDay: [],
    bySource: [],
    byDevice: [],
    topArticles: [],
  });
  const [articleEventLoading, setArticleEventLoading] = useState(false);
  const [aeCategoriesList, setAeCategoriesList] = useState([]);
  const [aeDateFrom, setAeDateFrom] = useState("");
  const [aeDateTo, setAeDateTo] = useState("");
  const [aeCategorie, setAeCategorie] = useState("all");
  const [aePage, setAePage] = useState(1);
  const [aeLimit] = useState(5);
  const [aeTotalPages, setAeTotalPages] = useState(1);
  const [aeTotal, setAeTotal] = useState(0);

  const { getGlobalStats, getArticleStatsTable, getArticleEventDetails } =
    useArticleEvent();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalArticle, setModalArticle] = useState(null); // { articleId, titre }
  const [modalData, setModalData] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalPage, setModalPage] = useState(1);
  const [modalTotalPages, setModalTotalPages] = useState(1);
  const [modalTotal, setModalTotal] = useState(0);

  const breadcrumbItems = [
    { label: "Mon Compte", link: "/utilisateur/compte" },
    { label: "Dashboard Admin" },
  ];

  const formatDateTime = (d) => {
    if (!d) return "—";
    try {
      return new Date(d).toLocaleString("fr-FR");
    } catch {
      return "—";
    }
  };

  const normalized = (v) =>
    String(v ?? "")
      .toLowerCase()
      .trim();

  const statusLabel = (s) => {
    switch (Number(s)) {
      case 1:
        return "Payé";
      case 2:
        return "En attente";
      case 3:
        return "Échoué";
      case 4:
        return "Gratuit";
      default:
        return "—";
    }
  };
  const openDetailsModal = (articleId, titre) => {
    setModalArticle({ articleId, titre });
    setModalOpen(true);
    fetchModalDetails(articleId, 1);
  };

  const closeDetailsModal = () => {
    setModalOpen(false);
    setModalArticle(null);
    setModalData([]);
  };

  const fetchModalDetails = async (articleId, targetPage) => {
    setModalLoading(true);
    try {
      const res = await getArticleEventDetails(articleId, {
        page: targetPage,
        limit: 15,
      });
      setModalData(res?.data?.data || []);
      setModalTotalPages(res?.data?.totalPages || 1);
      setModalTotal(res?.data?.total || 0);
      setModalPage(targetPage);
    } catch (e) {
      console.error(e);
      setAlert?.({
        type: "danger",
        message: "Impossible de charger les détails de l'événement.",
      });
    } finally {
      setModalLoading(false);
    }
  };

  const EVENT_TYPE_LABELS = {
    1: "Vue",
    2: "Clic",
    3: "Contact",
    4: "Favori",
    5: "Appel",
    6: "WhatsApp",
    7: "Partage Facebook",
  };

  const statusClass = (s) => {
    switch (Number(s)) {
      case 1:
        return "kpiGood";
      case 2:
        return "kpiWarn";
      case 3:
        return "kpiBad";
      case 4:
        return "kpiGood";
      default:
        return "kpiWarn";
    }
  };

  const methodLabel = (m) =>
    Number(m) === 1
      ? "MVola"
      : Number(m) === 2
        ? "Orange Money"
        : Number(m) === 3
          ? "Airtel Money"
          : "Carte";

  const typeLabel = (t) =>
    Number(t) === 1 ? "Abonnement" : Number(t) === 2 ? "Boost" : "Réabonnement";

  /* ── Formatage du label de l'axe X ── */
  const formatStatLabel = (key, mode) => {
    if (!key) return "—";
    if (mode === "year" || mode === "month") return key;
    const parts = key.split("-");
    if (parts.length === 3) {
      const day = parts[2];
      const month = parseInt(parts[1], 10);
      return `${day} ${MONTHS_SHORT_FR[month - 1] || ""}`;
    }
    return key;
  };

  const yearOptions = useMemo(() => {
    const current = now.getFullYear();
    const years = [];
    for (let y = current; y >= current - 5; y--) years.push(y);
    return years;
  }, []);
  const fetchArticleStatsTable = async (targetPage = aePage) => {
    if (user.role !== 1 && user.role !== 3) return;
    setArticleTableLoading(true);
    try {
      const params = { page: targetPage, limit: aeLimit };
      if (aeDateFrom) params.dateFrom = aeDateFrom;
      if (aeDateTo) params.dateTo = aeDateTo;
      if (aeCategorie !== "all") params.categorie = aeCategorie;

      const res = await getArticleStatsTable(params);
      setArticleStatsTable(res?.data?.data || []);
      setAeTotalPages(res?.data?.totalPages || 1);
      setAeTotal(res?.data?.total || 0);
      setAePage(targetPage);
    } catch (e) {
      console.error(e);
      setAlert?.({
        type: "danger",
        message: "Impossible de charger le tableau des statistiques.",
      });
    } finally {
      setArticleTableLoading(false);
    }
  };

  /* ── Fetch dashboard ── */
  const fetchDashboard = async () => {
    setLoading(true);
    try {
      let ru = [];
      let ra = [];
      let rp = [];
      let rs = [];
      let rv = [];
      let sd = [];

      if (user.role === 1 || user.role === 3) {
        ru = await getAllUser();
        setUsers(
          Array.isArray(ru?.data?.data ?? ru?.data)
            ? (ru?.data?.data ?? ru?.data)
            : [],
        );

        ra = await findAllArticlesForCategory(1, {
          limit: 10,
          sort: "-createdAt",
        });
        setArticles(Array.isArray(ra?.data) ? ra.data : []);
        setArticlesTotal(Number(ra?.total ?? 0));

        rp = await getPaiements();
        const pd = rp?.data?.data ?? rp?.data ?? [];
        setPaiements(Array.isArray(pd) ? pd : []);

        rs = await fetchSignalements();
        // const sigData = rs?.data?.data ?? rs?.data ?? [];
        // setSignalements(Array.isArray(sigData) ? sigData : []);

        rv = await getStatsVisites({ dateDebut, dateFin });

        setVisites({
          total: rv?.total ?? 0,
          jour: rv?.jour ?? 0,
          mois: rv?.mois ?? 0,
          annee: rv?.annee ?? 0,
        });

        sd = rs?.data?.data ?? rs?.data ?? [];
        setSignalements(Array.isArray(sd) ? sd : []);
      }
    } catch (e) {
      console.log(e);
      setAlert?.({
        type: "danger",
        message: "Impossible de charger le dashboard admin.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    if (user.role === 1 || user.role===3 ) {
      getAllCategories()
        .then((res) => setAeCategoriesList(res?.data?.data || []))
        .catch((e) => console.error(e));
      fetchArticleStatsTable(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Fetch stats publication ── */
  const fetchPublicationStats = async () => {
    setStatsLoading(true);
    try {
      const params = {};
      if (customStart || customEnd) {
        if (customStart) params.startDate = customStart;
        if (customEnd) params.endDate = customEnd;
      } else {
        if (statMonth) params.month = statMonth;
        if (statYear) params.year = statYear;
      }

      const res = await getArticlePublicationStats(params);
      const rows = res?.data || [];
      const mode = res?.mode || "day";

      const data = rows
        .filter((r) => r?.period)
        .map((r) => ({
          key: r.period,
          label: formatStatLabel(r.period, mode),
          actif: r.actif || 0,
          enAttente: r.enAttente || 0,
          nonActif: r.nonActif || 0,
          total: r.total || 0,
        }));

      const total = res?.total ?? data.reduce((s, r) => s + r.total, 0);
      setPublicationStats({ data, total, mode });
    } catch (e) {
      console.error(e);
      setAlert?.({
        type: "danger",
        message: "Impossible de charger les statistiques de publication.",
      });
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchPublicationStats();
  }, [statMonth, statYear, customStart, customEnd]);

  const resetDateRange = () => {
    setCustomStart("");
    setCustomEnd("");
  };
  const resetMonthYear = () => {
    setStatMonth("");
    setStatYear("");
  };

  const resetArticleEventFilters = async () => {
    setAeDateFrom("");
    setAeDateTo("");
    setAeCategorie("all");

    setArticleTableLoading(true);
    try {
      const res = await getArticleStatsTable({ page: 1, limit: aeLimit });
      setArticleStatsTable(res?.data?.data || []);
      setAeTotalPages(res?.data?.totalPages || 1);
      setAeTotal(res?.data?.total || 0);
      setAePage(1);
    } catch (e) {
      console.error(e);
      setAlert?.({
        type: "danger",
        message: "Impossible de charger le tableau des statistiques.",
      });
    } finally {
      setArticleTableLoading(false);
    }
  };
  /* ── KPIs ── */
  const kpis = useMemo(() => {
    const paiementRevenue = paiements
      .filter((p) => Number(p?.status) === 1)
      .reduce((sum, p) => sum + Number(p?.amount || 0), 0);
    return {
      userTotal: users.length,
      articleTotal:
        Number(articlesTotal) > 0 ? Number(articlesTotal) : articles.length,
      paiementTotal: paiements.length,
      paiementPaid: paiements.filter((p) => Number(p?.status) === 1).length,
      paiementPending: paiements.filter((p) => Number(p?.status) === 2).length,
      paiementFailed: paiements.filter((p) => Number(p?.status) === 3).length,
      paiementFree: paiements.filter((p) => Number(p?.status) === 4).length,
      paiementRevenue,
      signalTotal: (signalements || []).length,
    };
  }, [users, paiements, signalements, articlesTotal, articles]);

  const recentUsers = useMemo(
    () =>
      [...(users || [])]
        .sort(
          (a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0),
        )
        .slice(0, 5),
    [users],
  );

  const recentArticles = useMemo(
    () =>
      [...(articles || [])]
        .sort(
          (a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0),
        )
        .slice(0, 5),
    [articles],
  );

  const recentPaiements = useMemo(
    () =>
      [...(paiements || [])]
        .sort(
          (a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0),
        )
        .slice(0, 5),
    [paiements],
  );

  const recentSignalements = useMemo(
    () =>
      [...(signalements || [])]
        .sort(
          (a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0),
        )
        .slice(0, 5),
    [signalements],
  );

  /* ── Hauteur dynamique du chart selon nb de barres ── */
  const chartHeight = Math.max(
    220,
    Math.min(320, publicationStats.data.length * 18 + 100),
  );

  return (
    <div className="adminDashboardPage">
      <div className="container">
        <Breadcrumb items={breadcrumbItems} />

        {/* Header */}
        <div className="adminDashboardHeader">
          <div>
            <div className="adminDashboardTitle">Dashboard Admin</div>
            <div className="adminDashboardSubtitle">
              {loading ? "Chargement..." : "Synthèse globale de la plateforme"}
            </div>
          </div>
          <button
            type="button"
            className="adminDashboardBtn adminDashboardBtnSoft"
            onClick={fetchDashboard}
            disabled={loading}
            title="Rafraîchir"
          >
            <i className="bi bi-arrow-clockwise" />
            <span>Rafraîchir</span>
          </button>
        </div>

        {/* KPI Cards */}
        <div className="adminDashboardKpis">
          <Link
            to={`${user.role === 1 ? "/admin/utilisateurs" : "#"}`}
            className="adminDashboardKpiCard"
          >
            <div className="kpiIcon kpiUsers">
              <i className="bi bi-people" />
            </div>
            <div className="kpiBody">
              <div className="kpiLabel">Utilisateurs</div>
              <div className="kpiValue">{kpis.userTotal}</div>
              <div className="kpiHint">Total inscrits</div>
            </div>
          </Link>

          <Link
            to={`${user.role === 1 ? "/admin/articles" : "#"}`}
            className="adminDashboardKpiCard"
          >
            <div className="kpiIcon kpiArticles">
              <i className="bi bi-box-seam" />
            </div>
            <div className="kpiBody">
              <div className="kpiLabel">Articles</div>
              <div className="kpiValue">{kpis.articleTotal}</div>
              <div className="kpiHint">Total publiés</div>
            </div>
          </Link>

          <Link
            to={`${user.role === 1 ? "/admin/paiements" : "#"}`}
            className="adminDashboardKpiCard"
          >
            <div className="kpiIcon kpiPaiements">
              <i className="bi bi-credit-card" />
            </div>
            <div className="kpiBody">
              <div className="kpiLabel">Paiements</div>
              <div className="kpiValue">{kpis.paiementTotal}</div>
              <div className="kpiHint">
                <span className={`kpiPill ${statusClass(1)}`}>
                  {kpis.paiementPaid} payés
                </span>
                <span className={`kpiPill ${statusClass(2)}`}>
                  {kpis.paiementPending} attente
                </span>
                <span className={`kpiPill ${statusClass(3)}`}>
                  {kpis.paiementFailed} échoués
                </span>
                <span className={`kpiPill ${statusClass(4)}`}>
                  {kpis.paiementFree} gratuits
                </span>
              </div>
            </div>
          </Link>

          <Link
            to={`${user.role === 1 ? "/admin/signalements" : "#"}`}
            className="adminDashboardKpiCard"
          >
            <div className="kpiIcon kpiSignal">
              <i className="bi bi-flag" />
            </div>
            <div className="kpiBody">
              <div className="kpiLabel">Signalements</div>
              <div className="kpiValue">{kpis.signalTotal}</div>
              <div className="kpiHint">À traiter</div>
            </div>
          </Link>

          <Link
            to={`${user.role === 1 ? "/admin/visites" : "#"}`}
            className="adminDashboardKpiCard"
          >
            <div className="kpiIcon kpiUsers">
              <i className="bi bi-eye" />
            </div>

            <div className="kpiBody">
              <div className="kpiLabel">Visites</div>
              <div className="kpiValue">{visites.total}</div>

              <div className="kpiHint">
                <span className="kpiPill kpiGood">
                  {visites.jour} aujourd’hui
                </span>
                <span className="kpiPill kpiWarn">{visites.mois} ce mois</span>
                <span className="kpiPill kpiGood">
                  {visites.annee} cette année
                </span>
              </div>
            </div>
          </Link>
        </div>

        {/* Revenue bar */}
        <div className="adminDashboardRevenue">
          <div className="revenueLeft">
            <div className="revenueTitle">Revenus (paiements payés)</div>
            <div className="revenueValue">
              {Number.isFinite(kpis.paiementRevenue)
                ? kpis.paiementRevenue.toLocaleString("fr-FR")
                : "0"}{" "}
              MGA
            </div>
            <div className="revenueHint">
              Somme des montants des paiements au statut "Payé".
            </div>
          </div>
          {user.role === 1 && (
            <div className="revenueRight">
              <Link
                to={`${user.role === 1 ? "/admin/paiements" : "#"}`}
                className="adminDashboardBtn adminDashboardBtnPrimary"
              >
                Voir paiements
              </Link>
            </div>
          )}
        </div>

        {/* ══════════════════════════════════════════
            Statistiques de publication — Recharts
        ══════════════════════════════════════════ */}
        <div className="adminDashboardPanel adminDashboardStatsPanel">
          <div className="panelHeader">
            <div>
              <div className="panelTitle">Statistiques de publication</div>
              <div className="panelSubtitle">
                {publicationStats.total} article(s) sur la période affichée
              </div>
            </div>

            <div className="statsControls">
              {/* Sélecteurs mois / année */}
              <div className="statsSelectGroup">
                <select
                  className="statsSelect"
                  value={statMonth}
                  onChange={(e) => {
                    setStatMonth(e.target.value);
                    resetDateRange();
                  }}
                >
                  <option value="">Tous les mois</option>
                  {MONTHS_LONG_FR.map((name, i) => (
                    <option key={i + 1} value={i + 1}>
                      {name}
                    </option>
                  ))}
                </select>

                <select
                  className="statsSelect"
                  value={statYear}
                  onChange={(e) => {
                    setStatYear(e.target.value);
                    resetDateRange();
                  }}
                >
                  <option value="">Toutes les années</option>
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              {/* Plage personnalisée */}
              <div className="statsDateRange">
                <label className="statsDateField">
                  <span>Du</span>
                  <input
                    type="date"
                    value={customStart}
                    onChange={(e) => {
                      setCustomStart(e.target.value);
                      resetMonthYear();
                    }}
                    max={customEnd || undefined}
                  />
                </label>
                <label className="statsDateField">
                  <span>Au</span>
                  <input
                    type="date"
                    value={customEnd}
                    onChange={(e) => {
                      setCustomEnd(e.target.value);
                      resetMonthYear();
                    }}
                    min={customStart || undefined}
                  />
                </label>
                {(customStart || customEnd) && (
                  <button
                    type="button"
                    className="statsResetBtn"
                    onClick={resetDateRange}
                    title="Réinitialiser les dates"
                  >
                    <i className="bi bi-x-circle" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Légende */}
          <div className="statsLegend">
            <CustomLegend />
          </div>

          {/* Chart */}
          {statsLoading ? (
            <div className="panelEmpty">Chargement…</div>
          ) : publicationStats.data.length === 0 ? (
            <div className="panelEmpty">
              Aucune publication sur cette période.
            </div>
          ) : (
            <div className="statsChartWrap" style={{ padding: "12px 0 4px" }}>
              <ResponsiveContainer width="100%" height={chartHeight}>
                <BarChart
                  data={publicationStats.data}
                  margin={{ top: 4, right: 16, left: -16, bottom: 4 }}
                  barCategoryGap="30%"
                  barGap={3}
                >
                  <CartesianGrid
                    vertical={false}
                    stroke="rgba(0,0,0,0.06)"
                    strokeDasharray="0"
                  />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    axisLine={false}
                    tickLine={false}
                    width={35}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "rgba(0,0,0,0.04)" }}
                  />
                  <Bar
                    dataKey="actif"
                    name="Activé"
                    fill="#22c55e"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={24}
                    label={{
                      position: "top",
                      fontSize: 10,
                      fill: "#22c55e",
                      fontWeight: 600,
                    }}
                  />
                  <Bar
                    dataKey="enAttente"
                    name="En attente"
                    fill="#f59e0b"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={24}
                    label={{
                      position: "top",
                      fontSize: 10,
                      fill: "#f59e0b",
                      fontWeight: 600,
                    }}
                  />
                  <Bar
                    dataKey="nonActif"
                    name="Non activé"
                    fill="#ef4444"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={24}
                    label={{
                      position: "top",
                      fontSize: 10,
                      fill: "#ef4444",
                      fontWeight: 600,
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {(user.role === 1 || user.role === 3) && (
          <div className="adminDashboardPanel adminDashboardStatsPanel">
            <div className="panelHeader">
              <div>
                <div className="panelTitle">Statistiques des annonces</div>
                <div className="panelSubtitle">
                  Sources, campagnes et appareils des visiteurs
                </div>
              </div>

              <div className="statsControls">
                <div className="statsDateRange">
                  <label className="statsDateField">
                    <span>Du</span>
                    <input
                      type="date"
                      value={aeDateFrom}
                      onChange={(e) => setAeDateFrom(e.target.value)}
                    />
                  </label>
                  <label className="statsDateField">
                    <span>Au</span>
                    <input
                      type="date"
                      value={aeDateTo}
                      onChange={(e) => setAeDateTo(e.target.value)}
                    />
                  </label>
                </div>

                <select
                  className="statsSelect"
                  value={aeCategorie}
                  onChange={(e) => setAeCategorie(e.target.value)}
                >
                  <option value="all">Toutes catégories</option>
                  {aeCategoriesList.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.nom}
                    </option>
                  ))}
                </select>

                <div className="statsActionsRow">
                  <button
                    type="button"
                    className="adminDashboardBtn adminDashboardBtnPrimary"
                    onClick={() => fetchArticleStatsTable(1)}
                    disabled={articleTableLoading}
                  >
                    Appliquer
                  </button>
                  {(aeDateFrom || aeDateTo || aeCategorie !== "all") && (
                    <button
                      type="button"
                      className="statsResetBtn"
                      onClick={resetArticleEventFilters}
                      title="Réinitialiser les filtres"
                      disabled={articleTableLoading}
                    >
                      <i className="bi bi-x-circle" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {articleTableLoading ? (
              <div className="panelEmpty">Chargement…</div>
            ) : articleStatsTable.length === 0 ? (
              <div className="panelEmpty">Aucune donnée sur cette période.</div>
            ) : (
              <div className="statsAnnoncesTableWrap" style={{ marginTop: 12 }}>
                <table className="statsAnnoncesTable">
                  <thead>
                    <tr>
                      <th className="colSticky">Article</th>
                      <th className="colNum">Vues</th>
                      <th>Catégorie</th>
                      <th>Coupon</th>
                      <th>Dernière activité</th>
                      <th>Visiteurs identifiés</th>
                      <th className="colNum">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {articleStatsTable.map((row) => (
                      <tr key={row.articleId}>
                        <td className="panelStrong colSticky" title={row.titre}>
                          {row.titre}
                        </td>
                        <td className="colNum" data-label="Vues">
                          <span className="statBadge statBadgeViews">
                            {row.totalVues}
                          </span>
                        </td>
                        <td
                          className="cellTruncate"
                          data-label="Catégorie"
                          title={row.categorieNom}
                        >
                          {row.categorieNom}
                        </td>
                        <td
                          className="cellTruncate"
                          data-label="Coupon"
                          title={row.coupon}
                        >
                          {row.coupon !== "—" ? (
                            <span className="statBadge statBadgeCampaign">
                              {row.coupon}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td
                          className="cellTruncate"
                          data-label="Dernière activité"
                        >
                          {formatDateTime(row.dernierEvenement)}
                        </td>
                        <td data-label="Visiteurs">
                          <div className="statBadgeStack">
                            <span className="statBadge statBadgeIdentified">
                              <i className="bi bi-person-check" />{" "}
                              {row.nbUtilisateursConnectes ?? 0} connecté
                              {(row.nbUtilisateursConnectes ?? 0) > 1
                                ? "s"
                                : ""}
                            </span>
                            <span className="statBadge statBadgeAnonymous">
                              <i className="bi bi-person" />{" "}
                              {row.nbVisiteursAnonymes ?? 0} non connecté
                              {(row.nbVisiteursAnonymes ?? 0) > 1 ? "s" : ""}
                            </span>
                          </div>
                        </td>
                        <td className="colNum">
                          <button
                            type="button"
                            className="tableActionBtn"
                            onClick={() =>
                              openDetailsModal(row.articleId, row.titre)
                            }
                            title="Voir le détail des événements"
                          >
                            <i className="bi bi-eye" /> Détails
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {modalOpen && (
                  <div className="aeModalOverlay" onClick={closeDetailsModal}>
                    <div
                      className="aeModal"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="aeModalHeader">
                        <div>
                          <div className="aeModalTitle">
                            {modalArticle?.titre}
                          </div>
                          <div className="aeModalSubtitle">
                            {modalTotal} événement(s) enregistré(s)
                          </div>
                        </div>
                        <button
                          type="button"
                          className="aeModalCloseBtn"
                          onClick={closeDetailsModal}
                          title="Fermer"
                        >
                          <i className="bi bi-x-lg" />
                        </button>
                      </div>

                      <div className="aeModalBody">
                        {modalLoading ? (
                          <div className="panelEmpty">Chargement…</div>
                        ) : modalData.length === 0 ? (
                          <div className="panelEmpty">
                            Aucun événement enregistré.
                          </div>
                        ) : (
                          <div className="aeModalTableWrap">
                            <table className="statsAnnoncesTable">
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th>Utilisateur</th>
                                  <th>Referrer</th>
                                  <th>Source</th>
                                  <th>Navigateur</th>
                                  <th>OS</th>
                                  <th>Appareil</th>
                                  <th>IP</th>
                                </tr>
                              </thead>
                              <tbody>
                                {modalData.map((ev) => (
                                  <tr key={ev._id}>
                                    <td
                                      className="cellTruncate"
                                      data-label="Date"
                                    >
                                      {formatDateTime(ev.date)}
                                    </td>
                                    <td data-label="Utilisateur">
                                      {ev.utilisateur ? (
                                        <span className="statBadge statBadgeIdentified">
                                          {ev.utilisateur.nom ||
                                            ev.utilisateur.email}
                                        </span>
                                      ) : (
                                        <span className="statBadge statBadgeAnonymous">
                                          Utilisateur Non Connecté
                                        </span>
                                      )}
                                    </td>
                                    <td
                                      className="cellTruncate"
                                      data-label="Referrer"
                                      title={ev.referrer}
                                    >
                                      {ev.referrer || "—"}
                                    </td>
                                    <td
                                      className="cellTruncate"
                                      data-label="Source"
                                      title={ev.source}
                                    >
                                      <span className="statBadge statBadgeSource">
                                        {ev.source || "—"}
                                      </span>
                                    </td>
                                    <td data-label="Navigateur">
                                      {ev.appareil?.navigateur || "—"}
                                    </td>
                                    <td data-label="OS">
                                      {ev.appareil?.os || "—"}
                                    </td>
                                    <td data-label="Appareil">
                                      <span className="statBadge statBadgeDevice">
                                        {ev.appareil?.type || "inconnu"}
                                      </span>
                                    </td>
                                    <td
                                      className="cellTruncate"
                                      data-label="IP"
                                    >
                                      {ev.ip || "—"}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>

                      {modalData.length > 0 && (
                        <div className="tablePagination">
                          <div className="tablePaginationInfo">
                            Page {modalPage} / {modalTotalPages}
                          </div>
                          <div className="tablePaginationBtns">
                            <button
                              type="button"
                              className="tablePageBtn"
                              onClick={() =>
                                fetchModalDetails(
                                  modalArticle.articleId,
                                  modalPage - 1,
                                )
                              }
                              disabled={modalPage <= 1 || modalLoading}
                            >
                              <i className="bi bi-chevron-left" />
                            </button>
                            <span className="tablePageCurrent">
                              {modalPage}
                            </span>
                            <button
                              type="button"
                              className="tablePageBtn"
                              onClick={() =>
                                fetchModalDetails(
                                  modalArticle.articleId,
                                  modalPage + 1,
                                )
                              }
                              disabled={
                                modalPage >= modalTotalPages || modalLoading
                              }
                            >
                              <i className="bi bi-chevron-right" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {articleStatsTable.length > 0 && (
                  <div className="tablePagination">
                    <div className="tablePaginationInfo">
                      {aeTotal} annonce(s) au total — page {aePage} /{" "}
                      {aeTotalPages}
                    </div>
                    <div className="tablePaginationBtns">
                      <button
                        type="button"
                        className="tablePageBtn"
                        onClick={() => fetchArticleStatsTable(aePage - 1)}
                        disabled={aePage <= 1 || articleTableLoading}
                      >
                        <i className="bi bi-chevron-left" />
                      </button>
                      <span className="tablePageCurrent">{aePage}</span>
                      <button
                        type="button"
                        className="tablePageBtn"
                        onClick={() => fetchArticleStatsTable(aePage + 1)}
                        disabled={aePage >= aeTotalPages || articleTableLoading}
                      >
                        <i className="bi bi-chevron-right" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* 2×2 grids */}
        <div className="adminDashboardGrid">
          {/* Users */}
          <div className="adminDashboardPanel">
            <div className="panelHeader">
              <div>
                <div className="panelTitle">Derniers utilisateurs</div>
                <div className="panelSubtitle">5 plus récents</div>
              </div>
              <Link to="/admin/utilisateurs" className="panelLink">
                Voir tout <i className="bi bi-arrow-right" />
              </Link>
            </div>
            {loading ? (
              <div className="panelEmpty">Chargement…</div>
            ) : recentUsers.length === 0 ? (
              <div className="panelEmpty">Aucun utilisateur.</div>
            ) : (
              <div className="panelList">
                {recentUsers.map((u) => {
                  const fullName =
                    `${u?.nom ?? ""} ${u?.prenom ?? ""}`.trim() || "—";
                  return (
                    <div className="panelRow" key={u?._id}>
                      <div className="panelMain">
                        <div className="panelStrong">{fullName}</div>
                        <div className="panelMuted">{u?.email || "—"}</div>
                      </div>
                      <div className="panelMeta">
                        {formatDateTime(u?.createdAt)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Articles */}
          <div className="adminDashboardPanel">
            <div className="panelHeader">
              <div>
                <div className="panelTitle">Derniers articles</div>
                <div className="panelSubtitle">5 plus récents</div>
              </div>
              <Link to="/admin/articles" className="panelLink">
                Voir tout <i className="bi bi-arrow-right" />
              </Link>
            </div>
            {loading ? (
              <div className="panelEmpty">Chargement…</div>
            ) : recentArticles.length === 0 ? (
              <div className="panelEmpty">Aucun article.</div>
            ) : (
              <div className="panelList">
                {recentArticles.map((a) => {
                  const titre = a?.titre || "—";
                  const vendeur =
                    a?.idUtilisateur?.nom || a?.utilisateur?.nom
                      ? `${a?.idUtilisateur?.nom || a?.utilisateur?.nom || ""} ${
                          a?.idUtilisateur?.prenom ||
                          a?.utilisateur?.prenom ||
                          ""
                        }`.trim()
                      : a?.pseudo || "—";
                  return (
                    <div className="panelRow" key={a?._id}>
                      <div className="panelMain">
                        <div className="panelStrong">{titre}</div>
                        <div className="panelMuted">
                          {vendeur} • {a?.prix ?? "—"} Ar
                        </div>
                      </div>
                      <div className="panelMeta">
                        {formatDateTime(a?.createdAt)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Paiements */}
          <div className="adminDashboardPanel">
            <div className="panelHeader">
              <div>
                <div className="panelTitle">Derniers paiements</div>
                <div className="panelSubtitle">5 plus récents</div>
              </div>
              <Link to="/admin/paiements" className="panelLink">
                Voir tout <i className="bi bi-arrow-right" />
              </Link>
            </div>
            {loading ? (
              <div className="panelEmpty">Chargement…</div>
            ) : recentPaiements.length === 0 ? (
              <div className="panelEmpty">Aucun paiement.</div>
            ) : (
              <div className="panelList">
                {recentPaiements.map((p) => {
                  const fullName =
                    `${p?.idUser?.nom ?? ""} ${p?.idUser?.prenom ?? ""}`.trim() ||
                    "—";
                  return (
                    <div className="panelRow" key={p?._id}>
                      <div className="panelMain">
                        <div className="panelStrong">
                          {fullName} •{" "}
                          {Number(p?.amount || 0).toLocaleString("fr-FR")}{" "}
                          {p?.devise || "MGA"}
                        </div>
                        <div className="panelMuted">
                          {typeLabel(p?.type)} • {methodLabel(p?.methodPayment)}{" "}
                          •{" "}
                          <span className={`pill ${statusClass(p?.status)}`}>
                            {statusLabel(p?.status)}
                          </span>
                        </div>
                      </div>
                      <div className="panelMeta">
                        {formatDateTime(p?.createdAt)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Signalements */}
          <div className="adminDashboardPanel">
            <div className="panelHeader">
              <div>
                <div className="panelTitle">Signalements récents</div>
                <div className="panelSubtitle">5 plus récents</div>
              </div>
              <Link to="/admin/signalements" className="panelLink">
                Voir tout <i className="bi bi-arrow-right" />
              </Link>
            </div>
            {loading ? (
              <div className="panelEmpty">Chargement…</div>
            ) : recentSignalements.length === 0 ? (
              <div className="panelEmpty">Aucun signalement.</div>
            ) : (
              <div className="panelList">
                {recentSignalements.map((s) => {
                  const titre =
                    s?.idArticle?.titre || s?.article?.titre || "Article —";
                  const reason =
                    s?.motif || s?.raison || s?.message || "Signalement";
                  const reporter =
                    s?.idUser?.email || s?.user?.email || s?.email || "—";
                  return (
                    <div className="panelRow" key={s?._id}>
                      <div className="panelMain">
                        <div className="panelStrong">{titre}</div>
                        <div className="panelMuted">
                          {normalized(reason).slice(0, 80) || "—"} • {reporter}
                        </div>
                      </div>
                      <div className="panelMeta">
                        {formatDateTime(s?.createdAt)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {user.role === 1 && (
          <div className="adminDashboardQuick">
            <Link to="/admin/utilisateurs" className="quickBtn">
              <i className="bi bi-people" /> Gérer utilisateurs
            </Link>
            <Link to="/admin/articles" className="quickBtn">
              <i className="bi bi-box-seam" /> Gérer articles
            </Link>
            <Link to="/admin/paiements" className="quickBtn">
              <i className="bi bi-credit-card" /> Gérer paiements
            </Link>
            <Link to="/admin/signalements" className="quickBtn">
              <i className="bi bi-flag" /> Gérer signalements
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
